import {
  EventType,
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, safeCombineLatest, where } from '@principle-theorem/shared';
import { map, switchMap } from 'rxjs/operators';
import { Roster } from '../../staffer/roster';
import { Staffer } from '../../staffer/staffer';
import {
  IPageVisitTrackingConfig,
  IPracticeSetupStepDefinition,
} from './practice-setup-step.types';
import { initRawSchema, toParagraphContent } from '@principle-theorem/editor';

const pageVisitTracking: IPageVisitTrackingConfig = {
  routes: ['settings/brand/{brandId}/staff'],
  validationMode: 'count',
};

export const rosterSetupStep: IPracticeSetupStepDefinition = {
  id: PracticeSetupStepId.RosterSetup,
  category: PracticeSetupStepCategory.GeneralPracticeSettings,
  title: 'Create Rosters, Breaks, and Pre-blocks',
  description: initRawSchema([
    toParagraphContent(
      `Create recurring rosters for practitioners, add breaks, and any pre-blocks. Rosters will dictate the bookable time for your practitioners and allow appointments to be booked with them. Rosters can be set to recur on a one, two, three, or four week cycle. Multiple rosters can be created per practitioner to accommodate more complex availability.`
    ),
    toParagraphContent(
      `Once Rosters have been created, breaks and pre-blocks can be added to accurately reflect practitioners' availability, and reserve periods of time for certain treatment categories.`
    ),
  ]),
  dependsOn: [PracticeSetupStepId.StaffSetup],
  getRoute: (brand, _practice) => `${brand.slug}/staff`,
  validate$: (practice$, _step, _options) => {
    return practice$.pipe(
      switchMap((practice) =>
        Staffer.practitionersByPractice$(practice).pipe(
          switchMap((practitioners) =>
            safeCombineLatest(
              practitioners.map((practitioner) =>
                Firestore.query$(
                  Roster.col(practitioner),
                  where('item.event.type', 'in', [EventType.RosteredOn])
                ).pipe(
                  map((rosters) => {
                    const hasRosters =
                      rosters.filter((breakSchedule) => !breakSchedule.deleted)
                        .length > 0;
                    return {
                      name: practitioner.user.name,
                      hasRosters,
                    };
                  })
                )
              )
            )
          ),
          map((practitioners) => {
            const practitionersWithoutRosters = practitioners.filter(
              ({ hasRosters }) => !hasRosters
            );

            if (practitionersWithoutRosters.length === 0) {
              return { valid: true };
            }

            const practitionerNames = practitionersWithoutRosters
              .map((practitioner) => practitioner.name)
              .join(', ');
            return {
              valid: false,
              reason: `The following practitioners do not have any rosters: ${practitionerNames}.`,
              progress: {
                current:
                  practitioners.length - practitionersWithoutRosters.length,
                total: practitioners.length,
              },
            };
          })
        )
      )
    );
  },
  pageVisitTracking,
};
